import Header from "../components/Header";
import Footer from "../components/Footer";
import PageHero from "../components/PageHero";
import CTABand from "../components/CTABand";
import { TEAM } from "../data/leadership";
import { User } from "lucide-react";
import { PHOTOS } from "../data/photos";

// Display order: Board section LAST per client request
const ORDERED_TEAM = (() => {
  const board = TEAM.filter((t) => t.id === "board");
  const rest = TEAM.filter((t) => t.id !== "board");
  return [...rest, ...board];
})();

const MemberCard = ({ m }) => (
  <article className="rc-card-hover bg-white border border-[hsl(var(--border))] rounded-2xl overflow-hidden flex flex-col">
    <div className="relative aspect-[1/1] bg-[hsl(var(--rc-purple-soft))] overflow-hidden">
      {m.image ? (
        <img src={m.image} alt={m.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-20 h-20 rounded-full bg-white/70 flex items-center justify-center">
            <User className="w-9 h-9 text-[hsl(var(--rc-purple))]" />
          </div>
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-[hsl(var(--rc-gold))]" />
    </div>
    <div className="p-5 flex-1 flex flex-col">
      <h3 className="font-serif-display text-[18px] font-semibold text-[hsl(var(--rc-deep))] tracking-tight">
        {m.name}
      </h3>
      <div className="mt-1 text-[12.5px] font-medium text-[hsl(var(--rc-purple))]">
        {m.role}
      </div>
      <div className="mt-3 pt-3 border-t border-[hsl(var(--border))] text-[12px] italic text-[hsl(var(--muted-foreground))] flex-1">
        “{m.tag}”
      </div>
    </div>
  </article>
);

const LeadershipPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        <PageHero
          eyebrow="Our Team"
          title="Leadership & Staff"
          description="The leadership team for Recovery Connections of Central Florida is comprised of volunteers and peer specialists who dedicate their time, talent and treasure to building a Recovery Community Organization that connects Central Florida with the support and resources necessary for recovery."
          breadcrumbs={[{ label: "About Us", href: "/about" }, { label: "Leadership & Staff" }]}
          image={PHOTOS.warmConversation}
        />

        {ORDERED_TEAM.map((section, idx) => (
          <section
            key={section.id}
            id={section.id}
            className={`py-16 md:py-20 ${idx % 2 === 0 ? "bg-white" : "rc-section-soft"}`}
          >
            <div className="max-w-7xl mx-auto px-5 md:px-8">
              <div className="max-w-3xl mb-10">
                <p className="text-[12px] tracking-[0.22em] uppercase text-[hsl(var(--rc-purple))] font-semibold">
                  Section 0{idx + 1}
                </p>
                <h2 className="font-serif-display mt-2 text-[30px] md:text-[38px] leading-[1.15] font-semibold text-[hsl(var(--rc-deep))]">
                  {section.title}
                </h2>
                {section.description && (
                  <p className="mt-3 text-[15.5px] leading-[1.7] text-[hsl(var(--rc-slate))]">
                    {section.description}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                {section.members.map((m) => (
                  <MemberCard key={m.name} m={m} />
                ))}
              </div>
            </div>
          </section>
        ))}

        <CTABand
          eyebrow="Join our team"
          title="Become a Certified Recovery Peer Specialist."
          body="We train and credential the next generation of CRPS professionals. If you have lived experience and a desire to give back, we’d love to hear from you."
          primary={{ label: "Contact our team", href: "/contact" }}
          secondary={{ label: "See our programs", href: "/programs" }}
        />
      </main>
      <Footer />
    </div>
  );
};

export default LeadershipPage;
